<template>
  <form class="contact-form" novalidate @submit.prevent="onSubmit">
    <div class="contact-form__field">
      <label for="contact-name">Your name</label>
      <input
        id="contact-name"
        v-model.trim="form.name"
        name="name"
        type="text"
        autocomplete="name"
        maxlength="32"
        :aria-invalid="v$.name.$dirty && v$.name.$error ? 'true' : 'false'"
        :aria-describedby="
          v$.name.$dirty && v$.name.$error ? 'contact-name-error' : undefined
        "
        placeholder="First and last name"
      />
      <p
        v-if="v$.name.$dirty && v$.name.$error"
        id="contact-name-error"
        class="contact-form__error"
      >
        Please enter between 3 and 32 characters.
      </p>
    </div>

    <div class="contact-form__field">
      <label for="contact-email">Email address</label>
      <input
        id="contact-email"
        v-model.trim="form.email"
        name="email"
        type="email"
        autocomplete="email"
        :aria-invalid="v$.email.$dirty && v$.email.$error ? 'true' : 'false'"
        :aria-describedby="
          v$.email.$dirty && v$.email.$error ? 'contact-email-error' : undefined
        "
        placeholder="you@example.com"
      />
      <p
        v-if="v$.email.$dirty && v$.email.$error"
        id="contact-email-error"
        class="contact-form__error"
      >
        Please enter a valid email address.
      </p>
    </div>

    <div class="contact-form__field">
      <label for="contact-message">Message</label>
      <textarea
        id="contact-message"
        v-model.trim="form.message"
        name="message"
        rows="7"
        minlength="10"
        maxlength="3000"
        :aria-invalid="
          v$.message.$dirty && v$.message.$error ? 'true' : 'false'
        "
        :aria-describedby="
          v$.message.$dirty && v$.message.$error
            ? 'contact-message-error'
            : 'contact-message-help'
        "
        placeholder="Tell us how we can help. Include your order number when relevant."
      />
      <p
        v-if="v$.message.$dirty && v$.message.$error"
        id="contact-message-error"
        class="contact-form__error"
      >
        Please enter between 10 and 3000 characters.
      </p>
      <p v-else id="contact-message-help" class="contact-form__help">
        Please don’t include payment card information.
      </p>
    </div>

    <div class="contact-form__footer">
      <button type="submit" :disabled="isSubmitting">
        {{ isSubmitting ? "Sending…" : "Send message" }}
      </button>
      <p>
        By submitting, you agree that we may use your details to respond to your
        request. See our
        <a href="/privacy_policy">Privacy Policy</a>.
      </p>
    </div>

    <p
      v-if="successMessage"
      class="contact-form__status contact-form__status--success"
      role="status"
    >
      {{ successMessage }}
    </p>
    <p
      v-if="errorMessage"
      class="contact-form__status contact-form__status--error"
      role="alert"
    >
      {{ errorMessage }}
    </p>
  </form>
</template>
<script lang="ts" setup>
import { useVuelidate } from "@vuelidate/core";
import { required, minLength, maxLength, email } from "@vuelidate/validators";
import { reactive, nextTick, ref } from "vue";
import { useStore } from "vuex";
const form = reactive({
  name: "",
  email: "",
  message: "",
});
const isSubmitting = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const v$ = useVuelidate(
  {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(32),
    },
    email: {
      required,
      email,
    },
    message: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(3000),
    },
  },
  form
);
const store = useStore();
const onSubmit = async () => {
  if (isSubmitting.value) return;

  successMessage.value = "";
  errorMessage.value = "";
  v$.value.$touch();

  if (!v$.value.$invalid) {
    isSubmitting.value = true;
    try {
      const status = await store.dispatch("common/contact/send", {
        name: form.name,
        email: form.email,
        message: form.message,
      });
      if (status) {
        form.email = "";
        form.name = "";
        form.message = "";
        successMessage.value =
          "Thanks — your message was sent. We’ll reply within one business day.";

        nextTick(() => {
          v$.value.$reset();
        });
      } else {
        errorMessage.value =
          "We couldn’t send your message. Please try again or email support@hooliganseyewear.com.";
      }
    } catch {
      errorMessage.value =
        "We couldn’t send your message. Please try again or email support@hooliganseyewear.com.";
    } finally {
      isSubmitting.value = false;
    }
  }
};
</script>
